import React, { useState, FormEvent, ChangeEvent, JSX } from "react";
import api from "../api";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Lock,
    User,
    Loader2,
    AlertCircle,
    Eye,
    EyeOff,
    ArrowRight,
    HardHat,
} from "lucide-react";

interface LoginResponse {
    token: string;
    user: {
        id: string;
        username: string;
        email?: string;
    };
}

export default function Login(): JSX.Element {
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const nav = useNavigate();

    // Corporate Brand Colors
    const brandBlue = "#0054A6";
    const brandYellow = "#FFD100";

    const handleLogin = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        try {
            const res = await api.post<LoginResponse>("/auth/login", {
                username,
                password,
            });
            localStorage.setItem("token", res.data.token);
            nav("/profile");
        } catch (err: any) {
            const message =
                err.response?.data?.message || "Connection failed. Please try again.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0a0f1d] relative overflow-hidden font-sans">
            {/* Structural Background Accents */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
                <div 
                    className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full blur-[120px]" 
                    style={{ backgroundColor: `${brandBlue}33` }} 
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-[540px] px-6"
            >
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-8 md:p-10">
                    {/* Header with Logo */}
                    <header className="flex flex-col items-center mb-10">
                        <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
                            <img
                                src="/logo.jpeg"
                                alt="Kabbani Construction Group"
                                className="h-16 object-contain"
                            />
                        </div>
                        <h1 className="text-xl font-black text-white uppercase tracking-wider text-center mb-4">
                            Attendance Management System
                        </h1>
                        <h1 className="text-2xl font-black text-white uppercase tracking-wider text-center">
                            Sign In
                        </h1>
                        <div className="h-1 w-12 mt-2" style={{ backgroundColor: brandYellow }} />
                    </header>

                    {/* Error Message */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                className="mb-6 p-4 rounded-lg bg-red-500/10 border-l-4 border-red-500 text-red-200 text-sm flex items-center gap-3"
                            >
                                <AlertCircle size={18} className="shrink-0" />
                                <span>{error}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={handleLogin} className="space-y-6">
                        {/* Username Field */}
                        <div className="group">
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1.5 ml-1 tracking-widest">
                                Username
                            </label>
                            <div className="relative flex items-center">
                                <User
                                    className="absolute left-4 text-slate-500 group-focus-within:text-[#FFD100] transition-colors"
                                    size={19}
                                />
                                <input
                                    required
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-slate-700 text-white pl-12 pr-4 py-3.5 rounded-xl focus:border-[#FFD100] outline-none transition-all placeholder:text-slate-600 text-sm shadow-inner"
                                    placeholder="Enter your username"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="group">
                            <div className="flex justify-between items-center mb-1.5 px-1">
                                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                                    Password
                                </label>
                                <button
                                    type="button"
                                    style={{ color: brandYellow }}
                                    className="text-[11px] font-bold hover:underline transition-all uppercase tracking-tighter"
                                >
                                    Forgot?
                                </button>
                            </div>
                            <div className="relative flex items-center">
                                <Lock
                                    className="absolute left-4 text-slate-500 group-focus-within:text-[#FFD100] transition-colors"
                                    size={19}
                                />
                                <input
                                    required
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-slate-700 text-white pl-12 pr-12 py-3.5 rounded-xl focus:border-[#FFD100] outline-none transition-all placeholder:text-slate-600 text-sm shadow-inner"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 text-slate-500 hover:text-white transition-colors"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            disabled={isLoading}
                            type="submit"
                            style={{ backgroundColor: brandBlue }}
                            className="w-full group text-white font-bold py-4 rounded-xl shadow-lg hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3 border-b-4 border-black/20"
                        >
                            {isLoading ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                <>
                                    SIGN IN TO PORTAL
                                    <HardHat size={18} className="text-[#FFD100]" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <footer className="mt-10 text-center pt-8 border-t border-white/5">
                        <p className="text-slate-400 text-sm">
                            New to the Group?{" "}
                            <Link
                                to="/register"
                                className="font-bold hover:underline transition-colors ml-1"
                                style={{ color: brandYellow }}
                            >
                                Create Corporate Account
                            </Link>
                        </p>
                    </footer>
                </div>
            </motion.div>
        </div>
    );
}